import { Button } from "@/components/ui/button";
import { Download, FileText, Wrench } from "lucide-react";
import { jsPDF } from "jspdf";
import { toast } from "sonner";

interface InstallationGuidePDFProps {
  product: "K2" | "S1" | "M3";
  className?: string;
}

const k2InstallationData = {
  name: "K2 Quadrate Smart Toilet",
  tagline: "Revolutionary Square Design",
  dimensions: {
    length: "660mm",
    width: "400mm", 
    height: "520mm",
    weight: "45kg (approx.)",
  },
  plumbing: [
    { label: "Water Supply", value: "Cold water 1/2\" (G1/2) standard fitting" },
    { label: "Water Pressure", value: "0.05-0.75 MPa (no limit with built-in tank)" },
    { label: "Drain Type", value: "S-trap floor drain" },
    { label: "Rough-in Distance", value: "300-400mm" },
    { label: "Drain Pipe Diameter", value: "100mm standard" },
  ],
  electrical: [
    { label: "Voltage", value: "220V / 50Hz" },
    { label: "Max Power", value: "1200W" },
    { label: "Circuit Breaker", value: "16A recommended" },
    { label: "Outlet Type", value: "Grounded GFCI within 1m" },
    { label: "Waterproof Rating", value: "IPX4" },
  ],
  steps: [
    { title: "Preparation", desc: "Turn off water supply. Remove old toilet. Clean floor surface and inspect drain pipe condition." },
    { title: "Position Flange", desc: "Install new wax ring or gasket on drain flange. Ensure proper alignment with 300-400mm rough-in." },
    { title: "Set Toilet", desc: "Carefully lower K2 onto flange. Press firmly to compress wax ring. Level unit using shims if needed." },
    { title: "Secure Base", desc: "Install floor bolts and tighten evenly. Apply silicone sealant around base edge for watertight seal." },
    { title: "Water Connection", desc: "Connect braided supply line to inlet valve. Hand-tighten plus 1/4 turn with wrench. Check for leaks." },
    { title: "Electrical Setup", desc: "Plug power cord into grounded outlet. Ensure GFCI protection is active. Test power indicator." },
    { title: "System Test", desc: "Turn on water supply. Perform multiple test flushes. Check all bidet functions and heated seat operation." },
    { title: "Final Check", desc: "Verify no leaks at all connections. Test auto functions. Configure user preferences via control panel." },
  ],
  notes: [
    "Professional installation strongly recommended",
    "Ensure bathroom has adequate ventilation",
    "Keep electrical connections away from water",
    "Use only manufacturer-approved parts",
    "3-year warranty requires certified installation",
    "Minimum 150mm side clearance required",
    "Front clearance: 500mm recommended for comfort",
    "Allow 24 hours for silicone sealant to cure",
  ],
};

const s1InstallationData = {
  name: "S1 Super Egg Smart Toilet",
  tagline: "2022 MUSE Platinum Award Winner",
  dimensions: {
    length: "580mm",
    width: "430mm",
    height: "430mm",
    weight: "58kg",
  },
  plumbing: [
    { label: "Water Supply", value: "Cold water 1/2\" (G1/2) standard fitting" },
    { label: "Water Pressure", value: "No limit required (built-in intelligent tank)" },
    { label: "Drain Type", value: "S-trap floor drain" },
    { label: "Rough-in Distance", value: "300-400mm" },
    { label: "Flush Volume", value: "4L / 6L dual flush" },
  ],
  electrical: [
    { label: "Voltage", value: "220V / 50Hz" },
    { label: "Max Power", value: "1400W" },
    { label: "Circuit Breaker", value: "16A recommended" },
    { label: "Outlet Type", value: "Grounded GFCI within 1m" },
    { label: "Waterproof Rating", value: "IPX4" },
  ],
  steps: [
    { title: "Preparation", desc: "Turn off water supply. Remove old toilet. Clean floor surface. Note: S1 weighs 58kg - two people recommended." },
    { title: "Position Flange", desc: "Install new wax ring or gasket on drain flange. Verify 300-400mm rough-in distance matches S1 outlet." },
    { title: "Set Toilet", desc: "Carefully lower S1 onto flange with two people. Align egg shape with wall. Press firmly to seal. Level using shims." },
    { title: "Secure Base", desc: "Install and tighten floor bolts evenly. Apply silicone sealant around base for watertight seal. Allow 24h cure time." },
    { title: "Water Connection", desc: "Connect braided supply line to inlet valve. S1's built-in tank handles any pressure. Check connections for leaks." },
    { title: "Electrical Setup", desc: "Plug power cord into grounded GFCI outlet. Verify IPX4 protection. Test LED display powers on correctly." },
    { title: "System Test", desc: "Turn on water. Test dual flush (4L/6L). Verify booster pump operation. Test auto open/close and sensing functions." },
    { title: "Final Check", desc: "Test all wash modes (female, rear, massage, child). Verify 4-level seat heating. Configure preferences via rotary knob." },
  ],
  notes: [
    "Professional installation strongly recommended",
    "Two-person lift required (58kg weight)",
    "7-layer safety protection pre-installed",
    "Keep 20cm clear for auto-sensing to function",
    "3-year warranty requires certified installation",
    "Minimum 150mm side clearance required",
    "Front clearance: 500mm recommended",
    "Built-in booster pump - no external pressure needed",
  ],
};

const m3InstallationData = {
  name: "M3 Smart Toilet",
  tagline: "Voice Control & Radar Sensor Technology",
  dimensions: {
    length: "680mm",
    width: "380mm",
    height: "520mm",
    weight: "55kg (approx.)",
  },
  plumbing: [
    { label: "Water Supply", value: "Cold water 1/2\" (G1/2) standard fitting" },
    { label: "Water Pressure", value: "No limit required (built-in tank system)" },
    { label: "Drain Type", value: "S-trap ground drainage" },
    { label: "Rough-in Distance", value: "300-400mm" },
    { label: "Flush Volume", value: "4L / 6L intelligent dual flush" },
  ],
  electrical: [
    { label: "Voltage", value: "110V/220V ~ 50/60Hz" },
    { label: "Max Power", value: "1300W" },
    { label: "Circuit Breaker", value: "16A recommended" },
    { label: "Outlet Type", value: "Grounded GFCI within 1m" },
    { label: "Waterproof Rating", value: "IPX4" },
  ],
  steps: [
    { title: "Preparation", desc: "Turn off water supply. Remove old toilet. Clean floor surface. M3 weighs 55kg - two people recommended for lifting." },
    { title: "Position Flange", desc: "Install new wax ring or gasket on drain flange. Verify 300-400mm rough-in distance for S-trap outlet alignment." },
    { title: "Set Toilet", desc: "Carefully lower M3 onto flange with assistance. Align one-piece elongated design with wall. Press firmly to seal. Level using shims." },
    { title: "Secure Base", desc: "Install floor bolts and tighten evenly. Apply silicone sealant around base edge. Allow 24 hours for complete curing." },
    { title: "Water Connection", desc: "Connect braided supply line to inlet valve. Built-in tank handles any water pressure. Check all connections for leaks." },
    { title: "Electrical Setup", desc: "Plug power cord into grounded GFCI outlet. Verify instant heating display powers on. Test controller pairing." },
    { title: "Radar Sensor Setup", desc: "Clear 50cm front area for radar sensing. Test auto open/close lid function. Verify foot kick sensor responds correctly." },
    { title: "System Test", desc: "Test all wash modes (hip, female, self-cleaning). Verify seat heating reaches 36°C. Test warm air drying at all levels. Configure voice control." },
  ],
  notes: [
    "Professional installation strongly recommended",
    "Two-person lift required (55kg weight)",
    "Keep 50cm clear in front for radar sensor",
    "Voice control requires initial setup via controller",
    "3-year warranty requires certified installation",
    "Minimum 150mm side clearance required",
    "Front clearance: 500mm recommended for comfort",
    "Built-in tank - no external water pressure needed",
    "Deodorization system included - no additional setup",
    "Memory buttons store 2 user preferences",
  ],
};

export const InstallationGuidePDF = ({ product, className = "" }: InstallationGuidePDFProps) => {
  const data = product === "K2" ? k2InstallationData : product === "S1" ? s1InstallationData : m3InstallationData;
  
  const generatePDF = () => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);
      let yPos = margin;
      
      // Header
      doc.setFillColor(45, 55, 72);
      doc.rect(0, 0, pageWidth, 45, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.setFont("helvetica", "bold");
      doc.text("INSTALLATION GUIDE", margin, 22);
      
      doc.setFontSize(14);
      doc.setFont("helvetica", "normal");
      doc.text(data.name, margin, 32);
      
      doc.setFontSize(10);
      doc.text(data.tagline, margin, 40);
      
      // Company info on right
      doc.setFontSize(10);
      doc.text("Aureum VII Trading", pageWidth - margin, 22, { align: "right" });
      doc.text("www.aureum7trading.com", pageWidth - margin, 30, { align: "right" });
      doc.text("info@aureum7trading.com", pageWidth - margin, 38, { align: "right" });
      
      yPos = 55;
      
      // Product Dimensions
      doc.setTextColor(45, 55, 72);
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("PRODUCT DIMENSIONS", margin, yPos);
      yPos += 8;
      
      doc.setFillColor(248, 250, 252);
      doc.rect(margin, yPos, contentWidth, 25, 'F');
      
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 100, 100);
      
      const dimColWidth = contentWidth / 4;
      const dims = Object.entries(data.dimensions);
      dims.forEach(([key, value], i) => {
        const xPos = margin + (dimColWidth * i) + 5;
        doc.setFont("helvetica", "bold");
        doc.text(key.charAt(0).toUpperCase() + key.slice(1), xPos, yPos + 10);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(45, 55, 72);
        doc.text(value, xPos, yPos + 18);
        doc.setTextColor(100, 100, 100);
      });
      
      yPos += 35;
      
      // Plumbing Requirements
      doc.setTextColor(45, 55, 72);
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("PLUMBING REQUIREMENTS", margin, yPos);
      yPos += 8;
      
      doc.setFontSize(9);
      data.plumbing.forEach((item, i) => {
        const bgColor = i % 2 === 0 ? 248 : 255;
        doc.setFillColor(bgColor, bgColor === 248 ? 250 : 255, bgColor === 248 ? 252 : 255);
        doc.rect(margin, yPos, contentWidth / 2 - 2, 8, 'F');
        
        doc.setFont("helvetica", "bold");
        doc.setTextColor(100, 100, 100);
        doc.text(item.label + ":", margin + 3, yPos + 5.5);
        
        doc.setFont("helvetica", "normal");
        doc.setTextColor(45, 55, 72);
        doc.text(item.value, margin + 45, yPos + 5.5);
        
        yPos += 8;
      });
      
      // Electrical Requirements (beside plumbing)
      let elecYPos = yPos - (data.plumbing.length * 8);
      const elecXPos = margin + contentWidth / 2 + 2;
      
      data.electrical.forEach((item, i) => {
        const bgColor = i % 2 === 0 ? 248 : 255;
        doc.setFillColor(bgColor, bgColor === 248 ? 250 : 255, bgColor === 248 ? 252 : 255);
        doc.rect(elecXPos, elecYPos, contentWidth / 2 - 2, 8, 'F');
        
        doc.setFont("helvetica", "bold");
        doc.setTextColor(100, 100, 100);
        doc.text(item.label + ":", elecXPos + 3, elecYPos + 5.5);
        
        doc.setFont("helvetica", "normal");
        doc.setTextColor(45, 55, 72);
        doc.text(item.value, elecXPos + 40, elecYPos + 5.5);
        
        elecYPos += 8;
      });
      
      yPos += 10;
      
      // Installation Steps
      doc.setTextColor(45, 55, 72);
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("STEP-BY-STEP INSTALLATION", margin, yPos);
      yPos += 10;
      
      doc.setFontSize(9);
      data.steps.forEach((step, i) => {
        // Check if we need a new page
        if (yPos > 270) {
          doc.addPage();
          yPos = margin;
        }
        
        // Step number circle
        doc.setFillColor(45, 55, 72);
        doc.circle(margin + 5, yPos + 4, 4, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.text(String(i + 1), margin + 3.5, yPos + 5.5);
        
        // Step content
        doc.setTextColor(45, 55, 72);
        doc.setFont("helvetica", "bold");
        doc.text(`Step ${i + 1}: ${step.title}`, margin + 15, yPos + 5);
        
        doc.setFont("helvetica", "normal");
        doc.setTextColor(100, 100, 100);
        const splitDesc = doc.splitTextToSize(step.desc, contentWidth - 20);
        doc.text(splitDesc, margin + 15, yPos + 12);
        
        yPos += 10 + (splitDesc.length * 5);
      });
      
      // New page for Important Notes
      doc.addPage();
      yPos = margin;
      
      // Important Notes Header
      doc.setFillColor(254, 243, 199);
      doc.rect(margin, yPos, contentWidth, 12, 'F');
      doc.setTextColor(146, 64, 14);
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("⚠ IMPORTANT INSTALLATION NOTES", margin + 5, yPos + 8);
      yPos += 18;
      
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      data.notes.forEach((note, i) => {
        doc.setFont("helvetica", "normal");
        doc.text(`• ${note}`, margin + 5, yPos);
        yPos += 7;
      });
      
      yPos += 10;
      
      // Tools Required Section
      doc.setTextColor(45, 55, 72);
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("TOOLS REQUIRED", margin, yPos);
      yPos += 8;
      
      const tools = [
        "Adjustable wrench",
        "Screwdriver set (Phillips & flathead)",
        "Level",
        "Measuring tape",
        "Silicone sealant",
        "Wax ring or gasket",
        "Braided supply line",
        "Floor bolt set",
        "Shims (if needed)",
        "Clean cloth/rags",
      ];
      
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      const toolsPerRow = 2;
      tools.forEach((tool, i) => {
        const xOffset = (i % toolsPerRow) * (contentWidth / toolsPerRow);
        if (i > 0 && i % toolsPerRow === 0) yPos += 6;
        doc.text(`• ${tool}`, margin + xOffset + 5, yPos);
      });
      
      yPos += 20;
      
      // Contact & Support
      doc.setFillColor(240, 253, 250);
      doc.rect(margin, yPos, contentWidth, 35, 'F');
      
      doc.setTextColor(13, 148, 136);
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("NEED HELP? CONTACT US", margin + 5, yPos + 10);
      
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 100, 100);
      doc.text("Email: info@aureum7trading.com", margin + 5, yPos + 20);
      doc.text("WhatsApp: +34 642 278 939", margin + 5, yPos + 27);
      doc.text("Website: www.aureum7trading.com", margin + contentWidth / 2, yPos + 20);
      doc.text("Professional installer referrals available", margin + contentWidth / 2, yPos + 27);
      
      // Footer
      const footerY = doc.internal.pageSize.getHeight() - 10;
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`${data.name} Installation Guide | Aureum VII Trading | © ${new Date().getFullYear()}`, pageWidth / 2, footerY, { align: "center" });
      
      // Save the PDF
      doc.save(`${product}_Installation_Guide.pdf`);
      toast.success(`${data.name} installation guide downloaded!`);
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.error("Failed to generate PDF. Please try again.");
    }
  };
  
  return (
    <Button
      onClick={generatePDF}
      variant="outline"
      className={`gap-2 border-primary/30 hover:bg-primary/5 ${className}`}
    >
      <Wrench className="w-4 h-4" />
      <span>Download Installation Guide (PDF)</span>
      <Download className="w-4 h-4" />
    </Button>
  );
};

export default InstallationGuidePDF;
